import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShieldOff, LogOut } from 'lucide-react'
import { supabaseClient } from '../config/supabase'
import { useAuthStore } from '../store/authStore'

const EmployerDeactivatedPage: React.FC = () => {
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const [companyName, setCompanyName] = useState('')
  const [deactivationReason, setDeactivationReason] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabaseClient.auth.getUser()
      if (!user) {
        navigate('/login/employer')
        return
      }

      const { data } = await supabaseClient
        .from('employer_profiles')
        .select('deactivation_reason, companies(name)')
        .eq('user_id', user.id)
        .single()

      if (data) {
        setDeactivationReason(data.deactivation_reason ?? null)
        setCompanyName((data.companies as any)?.name || '')
      }
    } catch (err) {
      console.error('Error fetching deactivated profile:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login/employer')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center">
              <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News &amp; Updates</Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
              <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
            </nav>
            <button onClick={handleLogout} className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
              <LogOut className="w-4 h-4" /><span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white border border-red-200 rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <ShieldOff className="w-10 h-10 text-red-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Account Suspended</h1>
              <p className="text-gray-600 text-sm">
                {companyName
                  ? <><strong>{companyName}</strong>'s account has been deactivated by an administrator.</>
                  : 'Your account has been deactivated by an administrator.'
                }
              </p>
            </div>

            {deactivationReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-red-800 mb-1">Reason:</p>
                <p className="text-sm text-red-700">{deactivationReason}</p>
              </div>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                If you believe this is an error, please contact UCLM administration to appeal this decision.
              </p>
            </div>

            <button
              onClick={handleLogout}
              className="w-full bg-gray-700 hover:bg-gray-800 text-white py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <LogOut className="w-4 h-4" /><span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmployerDeactivatedPage
